// src/hooks/useLiff.js
import { useState, useEffect } from 'react';
import { ENV_CONFIG } from '../config';
import LiffService from '../services/liff';
import DebugLiffService from '../services/debugLiff';
import ApiService from '../services/api';

const useLiff = () => {
  const [isLiffReady, setIsLiffReady] = useState(false);
  const [liffError, setLiffError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [needsDebugLogin, setNeedsDebugLogin] = useState(false);
  const [isAuthRedirecting, setIsAuthRedirecting] = useState(false);

  const isDebugMode = ENV_CONFIG.DEBUG;
  const activeService = isDebugMode ? DebugLiffService : LiffService;

  useEffect(() => {
    // API認証エラー時のコールバックを設定
    ApiService.setAuthErrorCallback(() => {
      handleAuthError();
    });

    initializeLIFF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthError = () => {
    console.log('認証エラーが発生しました');
    setIsAuthRedirecting(true);

    if (isDebugMode) {
      // デバッグモードの場合、トークン再入力画面を表示
      setIsLiffReady(false);
      setNeedsDebugLogin(true);
      setLiffError(null);
      setIsAuthRedirecting(false);
    } else {
      // 本番モードの場合、LIFFの再ログインを試行
      console.log('LIFFログアウト後、再ログインを実行します');
      LiffService.logout();
      // 少し待ってからログイン（ログアウト処理の完了を待つ）
      setTimeout(() => {
        LiffService.login();
      }, 500);
    }
  };

  const initializeLIFF = async () => {
    try {
      console.log(`LIFF初期化開始 (${isDebugMode ? 'デバッグ' : '本番'}モード)`);

      if (isDebugMode) {
        // デバッグモードの場合、保存されたトークンで自動初期化を試行
        const autoInitialized = await DebugLiffService.tryAutoInitialize();

        if (!autoInitialized) {
          // 自動初期化に失敗した場合、トークン入力画面を表示
          setNeedsDebugLogin(true);
          setDebugInfo(DebugLiffService.getDebugInfo());
          return;
        }
      } else {
        // 本番モードの場合、通常のLIFF初期化
        await LiffService.initialize();
      }

      // activeServiceが準備完了しているかチェック
      if (activeService.ready) {
        console.log('LIFF初期化成功');
        setIsLiffReady(true);
        setNeedsDebugLogin(false);
      }

      // デバッグ情報を更新
      setDebugInfo(activeService.getDebugInfo());

    } catch (error) {
      console.error('LIFF initialization failed:', error);
      setLiffError(error.message);
      setDebugInfo(activeService.getDebugInfo());
    }
  };

  const handleDebugLogin = async (token) => {
    try {
      setLiffError(null);
      console.log('デバッグログイン開始');

      await DebugLiffService.initializeWithToken(token);

      if (DebugLiffService.ready) {
        console.log('デバッグログイン成功');
        setIsLiffReady(true);
        setNeedsDebugLogin(false);

        // デバッグ情報を更新
        setDebugInfo(DebugLiffService.getDebugInfo());
      }
    } catch (error) {
      console.error('デバッグログイン失敗:', error);
      throw error; // DebugLoginPageでエラーハンドリング
    }
  };

  const login = () => {
    if (isDebugMode) {
      setNeedsDebugLogin(true);
    } else {
      LiffService.login();
    }
  };

  const logout = () => {
    activeService.logout();
    setIsLiffReady(false);

    if (isDebugMode) {
      setNeedsDebugLogin(true);
    }
  };

  // 強制的にトークン再入力を求める（デバッグモード用）
  const forceReauth = () => {
    if (isDebugMode) {
      setIsLiffReady(false);
      setNeedsDebugLogin(true);
      DebugLiffService.clearDebugData();
    }
  };

  return {
    isLiffReady,
    liffError,
    debugInfo,
    needsDebugLogin: isDebugMode && needsDebugLogin,
    isDebugMode,
    isAuthRedirecting,
    login,
    logout,
    handleDebugLogin,
    forceReauth,
    liffService: activeService
  };
};

export default useLiff;